// DEPCHECKUI1 (docs/build-briefs/DEPCHECKUI1-system-checks-screens.md; DEC-612). Settings, System checks: the card at the top of
// the Settings area, and the red strip under the header of every manager screen.
//
// Both read the stored dependency checks of the device's location (GET .../locations/:locationId/system-checks, written by the
// hourly check-dependencies command) and the card can run them now (POST .../system-checks/run). The card is an admin surface:
// a keyboard and pointer are fine and every control is 44px or taller. All text goes in with textContent. The strip never blocks
// a screen: it reads in the background, keeps what it last knew when a read fails, and shows only what the server says is failing.

import {
  getSystemChecks,
  listTenantLocations,
  runSystemChecksNow,
  type LocationSystemChecksDto,
  type TenantLocationNameDto,
} from "./api.ts";
import {
  LOAD_FAILED_LINE,
  NO_CHECKS_LINE,
  NO_LOCATION_LINE,
  OPEN_SYSTEM_CHECKS_LABEL,
  RUNNING_CHECKS_LABEL,
  RUN_CHECKS_LABEL,
  RUN_FAILED_LINE,
  STALE_LINE,
  SYSTEM_CHECKS_HELP,
  SYSTEM_CHECKS_TITLE,
  SYSTEM_CHECK_NAMES,
  checkedAtText,
  orderedChecks,
  statusChip,
  stripText,
  summaryChip,
  whatToDo,
  type ChipView,
} from "./systemChecksModel.ts";

export interface SystemChecksDeps {
  apiBaseUrl: () => string;
  tenantId: () => string;
  /** The session holds admin.tenant_settings.view (the permission both routes require). */
  canView: () => boolean;
  /** The location this device belongs to, when it is set up as one. */
  deviceLocationId: () => string | undefined;
  /** True while the session this serves is still the live one. */
  isCurrent: () => boolean;
}

function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string | null,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(tag);
  if (className !== null) created.className = className;
  if (text !== undefined) created.textContent = text;
  return created;
}

export function chipNode(chip: ChipView, testid?: string): HTMLElement {
  const el = node("span", `fl-chip fl-chip-${chip.tone}`);
  el.append(node("span", "fl-chip-symbol", chip.symbol), document.createTextNode(` ${chip.label}`));
  el.firstElementChild?.setAttribute("aria-hidden", "true");
  if (testid !== undefined) el.dataset["testid"] = testid;
  return el;
}

/** The location the checks are about: this device's, else the customer's main location. Cached for the session's life. */
function createLocationResolver(deps: SystemChecksDeps): {
  resolve(): Promise<string | undefined>;
  reset(): void;
} {
  let cached: string | undefined;
  return {
    async resolve() {
      const device = deps.deviceLocationId();
      if (device !== undefined) return device;
      if (cached !== undefined) return cached;
      const listed = await listTenantLocations(deps.apiBaseUrl(), deps.tenantId());
      if (listed.status !== 200 || !Array.isArray(listed.body.locations)) return undefined;
      const found = listed.body.locations as TenantLocationNameDto[];
      cached = found.find((entry) => entry.isMain)?.id ?? found[0]?.id;
      return cached;
    },
    reset() {
      cached = undefined;
    },
  };
}

// ------------------------------------------------------------------------------------------------------------------- the card

export interface SystemChecksCard {
  refresh(): Promise<void>;
  reset(): void;
  /** Brings the card into view (the strip's link). */
  show(): void;
}

export function mountSystemChecksCard(
  root: HTMLElement,
  deps: SystemChecksDeps,
  hooks: { onResult: (view: LocationSystemChecksDto) => void },
): SystemChecksCard {
  const siteResolver = createLocationResolver(deps);
  let view: LocationSystemChecksDto | undefined;
  let status: "idle" | "loading" | "failed" | "no_location" = "idle";
  let running = false;
  let runFailed = false;
  let sequence = 0;
  root.hidden = true;

  function reset(): void {
    sequence += 1;
    siteResolver.reset();
    view = undefined;
    status = "idle";
    running = false;
    runFailed = false;
    root.replaceChildren();
    root.hidden = true;
  }

  async function refresh(): Promise<void> {
    if (!deps.canView()) {
      reset();
      return;
    }
    const mine = (sequence += 1);
    if (view === undefined) {
      status = "loading";
      render();
    }
    const locationId = await siteResolver.resolve();
    if (mine !== sequence || !deps.isCurrent()) return;
    if (locationId === undefined) {
      status = "no_location";
      view = undefined;
      render();
      return;
    }
    const result = await getSystemChecks(deps.apiBaseUrl(), deps.tenantId(), locationId);
    if (mine !== sequence || !deps.isCurrent()) return;
    if (result.status === 200 && Array.isArray(result.body.checks)) {
      view = result.body;
      status = "idle";
      runFailed = false;
      hooks.onResult(result.body);
    } else {
      // Known rows stay on the card when a refresh fails; the failure line is added beside them.
      status = "failed";
    }
    render();
  }

  async function run(): Promise<void> {
    if (running) return;
    const locationId = await siteResolver.resolve();
    if (locationId === undefined || !deps.isCurrent()) return;
    const mine = sequence;
    running = true;
    runFailed = false;
    render();
    const result = await runSystemChecksNow(deps.apiBaseUrl(), deps.tenantId(), locationId);
    if (mine !== sequence || !deps.isCurrent()) return;
    running = false;
    if (result.status === 200 && Array.isArray(result.body.checks)) {
      view = result.body;
      status = "idle";
      hooks.onResult(result.body);
    } else {
      runFailed = true;
    }
    render();
  }

  function failureBox(sentence: string, retry: () => void): HTMLElement {
    const box = node("div", "failure-state");
    box.setAttribute("role", "alert");
    box.append(
      node("span", "failure-state-symbol", "!"),
      node("span", "failure-state-text", sentence),
    );
    const again = node("button", "btn-secondary btn-compact failure-state-retry", "Try again");
    again.type = "button";
    again.addEventListener("click", retry);
    box.append(again);
    return box;
  }

  function checksTable(checks: LocationSystemChecksDto["checks"]): HTMLElement {
    const table = node("table", "fl-table syschk-table");
    table.dataset["testid"] = "system-checks-table";
    const head = node("tr", null);
    for (const label of ["Check", "Status", "What it means"]) {
      const th = node("th", "fl-plain", label);
      th.scope = "col";
      head.append(th);
    }
    const thead = node("thead", null);
    thead.append(head);
    const body = node("tbody", null);
    for (const check of orderedChecks(checks)) {
      const row = node("tr", check.status === "failing" ? "syschk-row-bad" : null);
      row.dataset["testid"] = "system-checks-row";
      row.dataset["check"] = check.key;
      row.dataset["status"] = check.status;
      const name = node("td", "fl-name syschk-name");
      name.append(node("span", "syschk-check-name", SYSTEM_CHECK_NAMES[check.key]));
      const state = node("td", "syschk-status");
      state.append(chipNode(statusChip(check.status), "system-checks-status"));
      const meaning = node("td", "fl-wrap-cell syschk-meaning");
      meaning.append(node("span", "syschk-detail", check.detail));
      const todo = whatToDo(check);
      if (todo !== "") meaning.append(node("span", "syschk-todo", todo));
      row.append(name, state, meaning);
      body.append(row);
    }
    table.append(thead, body);
    const wrap = node("div", "fl-wrap syschk-table-wrap");
    wrap.append(table);
    return wrap;
  }

  function render(): void {
    root.replaceChildren();
    root.hidden = false;
    const header = node("div", "syschk-header");
    const title = node("h2", "section-title syschk-title", SYSTEM_CHECKS_TITLE);
    title.id = "admin-system-checks-heading";
    header.append(title);
    const summary = chipNode(summaryChip(view), "system-checks-summary");
    summary.classList.add("syschk-summary");
    header.append(summary);
    const button = node(
      "button",
      "btn-secondary syschk-run",
      running ? RUNNING_CHECKS_LABEL : RUN_CHECKS_LABEL,
    );
    button.type = "button";
    button.disabled = running || status === "no_location";
    button.dataset["testid"] = "system-checks-run";
    button.addEventListener("click", () => void run());
    header.append(button);
    root.append(header);

    const help = node("p", "admin-field-help", SYSTEM_CHECKS_HELP);
    help.dataset["testid"] = "system-checks-help";
    root.append(help);

    if (status === "no_location") {
      root.append(node("p", "admin-field-help", NO_LOCATION_LINE));
      return;
    }
    if (view === undefined) {
      if (status === "failed") root.append(failureBox(LOAD_FAILED_LINE, () => void refresh()));
      else root.append(node("p", "syschk-quiet", "Loading..."));
      return;
    }
    const when = node("p", "syschk-when");
    when.dataset["testid"] = "system-checks-when";
    when.append(
      document.createTextNode(
        `${view.locationName}. ${checkedAtText(view.oldestCheckedAt, new Date())}.`,
      ),
    );
    if (view.stale && view.oldestCheckedAt !== null) {
      when.append(
        document.createTextNode(" "),
        chipNode({ label: "Out of date", symbol: "!", tone: "warn" }, "system-checks-stale"),
      );
    }
    root.append(when);
    if (view.stale && view.oldestCheckedAt !== null) {
      const stale = node("p", "syschk-quiet", STALE_LINE);
      stale.dataset["testid"] = "system-checks-stale-line";
      root.append(stale);
    }
    if (status === "failed") root.append(failureBox(LOAD_FAILED_LINE, () => void refresh()));
    if (runFailed) {
      const failed = node("p", "syschk-run-failed", RUN_FAILED_LINE);
      failed.setAttribute("role", "alert");
      failed.dataset["testid"] = "system-checks-run-failed";
      root.append(failed);
    }
    if (view.checks.length === 0) {
      const empty = node("p", "syschk-quiet", NO_CHECKS_LINE);
      empty.dataset["testid"] = "system-checks-empty";
      root.append(empty);
      return;
    }
    root.append(checksTable(view.checks));
  }

  return {
    refresh,
    reset,
    show() {
      if (root.hidden) return;
      root.scrollIntoView({ block: "start" });
    },
  };
}

// ------------------------------------------------------------------------------------------------------------------ the strip

/** Reads again at most this often. The page asks every five minutes; a screen change inside this window re-uses the last answer. */
export const STRIP_MIN_GAP_MS = 4 * 60_000;
export const STRIP_POLL_MS = 5 * 60_000;

export interface SystemChecksStripDeps extends SystemChecksDeps {
  /** Changes when a different person or location is signed in, so one session's answer is never shown to the next. */
  sessionKey: () => string;
  /** Opens Settings and brings the System checks card into view. */
  openCard: () => void;
}

export interface SystemChecksStrip {
  /** Called when a screen with the shared header is shown, and by the timer. Rate limited; never awaited by a screen. */
  sync(): void;
  /** The card read or ran the checks: use that answer now. */
  setResult(view: LocationSystemChecksDto): void;
  /** No screen with the header is showing, or the session ended. */
  hide(): void;
  reset(): void;
}

export function mountSystemChecksStrip(
  root: HTMLElement,
  deps: SystemChecksStripDeps,
): SystemChecksStrip {
  const siteResolver = createLocationResolver(deps);
  let failing: LocationSystemChecksDto["checks"] = [];
  let lastReadAt = 0;
  let inFlight = false;
  let sequence = 0;
  let shown = false;
  let key = "";
  root.hidden = true;

  function paint(): void {
    const sentence = shown ? stripText(failing) : "";
    if (sentence === "") {
      root.hidden = true;
      root.replaceChildren();
      return;
    }
    root.replaceChildren();
    const mark = node("span", "syschk-strip-symbol", "!");
    mark.setAttribute("aria-hidden", "true");
    const text = node("span", "syschk-strip-text", sentence);
    text.dataset["testid"] = "system-checks-strip-text";
    const link = node("button", "syschk-strip-link", OPEN_SYSTEM_CHECKS_LABEL);
    link.type = "button";
    link.dataset["testid"] = "system-checks-strip-open";
    link.addEventListener("click", () => deps.openCard());
    root.append(mark, text, link);
    root.hidden = false;
  }

  function reset(): void {
    sequence += 1;
    siteResolver.reset();
    failing = [];
    lastReadAt = 0;
    inFlight = false;
    shown = false;
    paint();
  }

  async function read(): Promise<void> {
    const mine = sequence;
    inFlight = true;
    try {
      const locationId = await siteResolver.resolve();
      if (mine !== sequence || locationId === undefined) return;
      const result = await getSystemChecks(deps.apiBaseUrl(), deps.tenantId(), locationId);
      if (mine !== sequence || !deps.isCurrent()) return;
      // A failed read keeps the last answer: not knowing is not the same as nothing failing, and a banner is not an error box.
      if (result.status === 200 && Array.isArray(result.body.checks)) {
        failing = result.body.checks.filter((check) => check.status === "failing");
        lastReadAt = Date.now();
        paint();
      }
    } finally {
      if (mine === sequence) inFlight = false;
    }
  }

  return {
    sync() {
      const current = deps.sessionKey();
      if (current !== key) {
        reset();
        key = current;
      }
      if (!deps.canView() || !deps.isCurrent()) {
        reset();
        return;
      }
      shown = true;
      paint();
      if (inFlight || Date.now() - lastReadAt < STRIP_MIN_GAP_MS) return;
      void read();
    },
    setResult(view) {
      failing = view.checks.filter((check) => check.status === "failing");
      lastReadAt = Date.now();
      paint();
    },
    hide() {
      shown = false;
      paint();
    },
    reset,
  };
}
